import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

import { ConversationSidebar } from "@/features/chat/ConversationSidebar";
import { submitAuditReview } from "@/lib/api/admin";
import { getConversation, submitChatQuery } from "@/lib/api/chat";
import type {
  AgentResponse,
  AgentTrace,
  ConversationDetail,
  DescriptionMatch,
  ProductPriceBySupplier,
  ProductPriceTrendPoint,
  ProductRanking,
  SupplierRanking,
  VerificationClaim,
} from "@/lib/api/chat";
import { listDataSources, type DataSource } from "@/lib/api/dataSources";

/**
 * Whether the heavier auxiliary tables (suppliers, products, price by
 * supplier, monthly trend) should render in the chat bubble. Gated on
 * the `?debug=1` URL param so the analyst-facing default stays clean
 * (prose + Resumen + Verificación + Similar descriptions + UNSPSC
 * breakdown). JP 2026-05-18: even intent-driven gating felt noisy —
 * "Estos datos solo se muestran si el usuario pide esa informacion".
 * Same toggle the DebugPanel already uses, so one URL flag flips the
 * entire developer-mode UI on at once.
 */
function isAdvancedViewOn(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).has("debug");
}

// Persist the analyst's pick across reloads. `null` means "every source the
// user can read" — the agent runs against the union of every ingested
// corpus. Stored as a string so we can use it as a React Query key directly.
const SOURCE_STORAGE_KEY = "b2b.chat.activeSourceId";
const SOURCE_ALL = "__all__";

function readStoredSource(): string {
  try {
    return localStorage.getItem(SOURCE_STORAGE_KEY) || SOURCE_ALL;
  } catch {
    return SOURCE_ALL;
  }
}

function writeStoredSource(value: string) {
  try {
    if (value === SOURCE_ALL) localStorage.removeItem(SOURCE_STORAGE_KEY);
    else localStorage.setItem(SOURCE_STORAGE_KEY, value);
  } catch {
    // Storage may be disabled; the selector still works for the session.
  }
}

interface Turn {
  query: string;
  agent: AgentResponse;
  responseId: string;
}

export function ChatPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState("");
  const [activeSourceId, setActiveSourceId] = useState<string>(() => readStoredSource());

  const dataSourcesQuery = useQuery<DataSource[], Error>({
    queryKey: ["data-sources", "for-chat"],
    queryFn: listDataSources,
    staleTime: 5 * 60_000,
  });
  const enabledSources = useMemo(
    () => (dataSourcesQuery.data ?? []).filter((s) => s.enabled),
    [dataSourcesQuery.data],
  );

  // Drop the persisted choice if the source has since been disabled or
  // deleted — otherwise the dropdown shows a stale id with no label.
  useEffect(() => {
    if (!dataSourcesQuery.data || activeSourceId === SOURCE_ALL) return;
    if (!enabledSources.some((s) => s.id === activeSourceId)) {
      setActiveSourceId(SOURCE_ALL);
      writeStoredSource(SOURCE_ALL);
    }
  }, [dataSourcesQuery.data, enabledSources, activeSourceId]);

  const onChangeSource = (next: string) => {
    setActiveSourceId(next);
    writeStoredSource(next);
  };

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const bottomAnchorRef = useRef<HTMLDivElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const conversationQuery = useQuery<ConversationDetail, Error>({
    queryKey: ["chat", "conversation", conversationId],
    queryFn: () =>
      getConversation(conversationId as string).then((detail) => {
        setTurns(
          detail.turns.map((t) => ({
            responseId: t.response_id,
            query: t.query,
            agent: t.agent,
          })),
        );
        return detail;
      }),
    enabled: conversationId !== null,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: (q: string) =>
      submitChatQuery(
        q,
        conversationId ?? undefined,
        activeSourceId === SOURCE_ALL ? null : activeSourceId,
      ),
    onSuccess: (data, query) => {
      setConversationId(data.conversation_id);
      setTurns((prev) => [...prev, { query, agent: data.agent, responseId: data.response_id }]);
      setInput("");
      void queryClient.invalidateQueries({ queryKey: ["chat", "conversations"] });
    },
  });

  useEffect(() => {
    bottomAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [turns.length, mutation.isPending]);

  const onSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const trimmed = input.trim();
    if (!trimmed || mutation.isPending) return;
    mutation.mutate(trimmed);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      onSubmit(event);
    }
  };

  const onPickConversation = (id: string | null) => {
    setConversationId(id);
    if (id === null) setTurns([]);
  };

  return (
    <div className="flex min-h-0 flex-1 overflow-hidden">
      <ConversationSidebar
        open={sidebarOpen}
        onToggle={() => setSidebarOpen((v) => !v)}
        activeConversationId={conversationId}
        onPick={onPickConversation}
      />

      <section className="relative flex min-w-0 flex-1 flex-col bg-pantera-50">
        {/* Conversation sub-header */}
        <div className="flex flex-shrink-0 items-center justify-between border-b border-pantera-100 bg-white px-6 py-3">
          <div>
            <h2 className="text-[15px] font-bold text-pantera-900">
              {conversationId ? t("chat.title") : t("chat.empty")}
            </h2>
            {conversationId && (
              <p className="mt-0.5 text-[11px] text-muted">
                {t("chat.conversation", { id: conversationId.slice(0, 8) })}
              </p>
            )}
          </div>
          <DataSourceSelector
            sources={enabledSources}
            value={activeSourceId}
            onChange={onChangeSource}
            loading={dataSourcesQuery.isLoading}
            disabled={mutation.isPending}
          />
        </div>

        {/* Chat thread */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-6 py-6 pb-48">
          {conversationQuery.isLoading && (
            <p className="text-sm text-muted">{t("chat.loadingConversation")}</p>
          )}
          {!conversationQuery.isLoading && turns.length === 0 && !mutation.isPending && (
            <EmptyState />
          )}

          <div className="mx-auto max-w-3xl space-y-6">
            {turns.map((turn) => (
              <div key={turn.responseId}>
                <UserBubble text={turn.query} latencyMs={turn.agent.latency_ms} />
                <AgentBubble agent={turn.agent} responseId={turn.responseId} />
              </div>
            ))}
            {mutation.isPending && (
              <div>
                <UserBubble text={input || mutation.variables || ""} latencyMs={null} />
                <PendingBubble />
              </div>
            )}
          </div>

          <div ref={bottomAnchorRef} />
        </div>

        {mutation.isError && (
          <div className="mx-6 mb-2 rounded-card border border-error/20 bg-error/5 p-3 text-xs text-error">
            {t("chat.error", { message: (mutation.error as Error)?.message ?? "" })}
          </div>
        )}

        {/* Input area — sticky gradient fade */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-pantera-50 via-pantera-50/95 to-transparent px-6 pb-7 pt-10">
          <form onSubmit={onSubmit} className="mx-auto max-w-3xl">
            <div className="flex items-center gap-3 rounded-[24px] border-[1.5px] border-pantera-200 bg-white pl-5 pr-2 py-2 shadow-card-lg focus-within:border-pantera-700 focus-within:ring-4 focus-within:ring-pantera-700/10 transition-all">
              <textarea
                ref={textareaRef}
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={onKeyDown}
                rows={1}
                placeholder={t("chat.placeholderInput")}
                className="flex-1 resize-none bg-transparent py-1.5 text-[14.5px] text-pantera-900 outline-none placeholder:text-muted"
                style={{ maxHeight: 120 }}
                disabled={mutation.isPending}
              />
              <button
                type="submit"
                disabled={mutation.isPending || input.trim().length === 0}
                className="flex-shrink-0 rounded-pill bg-cta-gradient px-5 py-2.5 text-sm font-bold text-white shadow-cta hover:opacity-95 transition disabled:opacity-40"
              >
                {mutation.isPending ? t("chat.sending") : t("chat.send")}
              </button>
            </div>
            <p className="mt-2 text-center text-[11px] text-muted/70">
              B2B Negocios puede cometer errores. Verifica información crítica.
            </p>
          </form>
        </div>
      </section>
    </div>
  );
}

function DataSourceSelector({
  sources,
  value,
  onChange,
  loading,
  disabled,
}: {
  sources: DataSource[];
  value: string;
  onChange: (next: string) => void;
  loading: boolean;
  disabled: boolean;
}) {
  const { t } = useTranslation();
  // Hide the control entirely when there's only the (legacy) single
  // source — pre-Sprint-0a-bis screens shouldn't grow a useless dropdown.
  if (!loading && sources.length <= 1) return null;

  return (
    <label className="flex items-center gap-2 text-[11px] text-muted">
      <span className="font-semibold uppercase tracking-[0.14em]">
        {t("chat.sourceSelector.label")}
      </span>
      <select
        className="rounded-pill border border-pantera-200 bg-white px-3 py-1.5 text-[12px] font-semibold text-pantera-900 outline-none focus:border-pantera-700 disabled:opacity-50"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || loading}
      >
        <option value={SOURCE_ALL}>{t("chat.sourceSelector.all")}</option>
        {sources.map((s) => (
          <option key={s.id} value={s.id}>
            {s.name}
          </option>
        ))}
      </select>
    </label>
  );
}

function EmptyState() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto flex min-h-[50vh] max-w-3xl flex-col items-center justify-center text-center">
      <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-[14px] bg-pantera-900">
        <SparkIcon />
      </div>
      <p className="text-lg font-bold text-pantera-900">{t("chat.empty")}</p>
      <p className="mt-2 max-w-sm text-sm text-muted">{t("chat.emptyHint")}</p>
    </div>
  );
}

function UserBubble({ text, latencyMs }: { text: string; latencyMs: number | null }) {
  const { t } = useTranslation();
  return (
    <div className="mb-2 flex justify-end">
      <div className="max-w-[70%]">
        <div className="rounded-[22px] rounded-br-[6px] bg-pantera-700 px-5 py-3 text-[14.5px] leading-relaxed text-white shadow-md">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.14em] text-pantera-200/80">
            {t("chat.you")}
            {latencyMs !== null && (
              <span className="ml-2 opacity-60">· {t("chat.latency", { ms: latencyMs })}</span>
            )}
          </div>
          <p className="whitespace-pre-line">{text}</p>
        </div>
      </div>
    </div>
  );
}

function PendingBubble() {
  const { t } = useTranslation();
  return (
    <div className="mt-2 flex gap-3">
      <AgentAvatar />
      <div className="flex items-center gap-2 rounded-[22px] rounded-bl-[6px] bg-white px-5 py-3.5 shadow-card">
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-pantera-700/40" />
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-pantera-700/70 [animation-delay:120ms]" />
        <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-pantera-700 [animation-delay:240ms]" />
        <span className="ml-2 text-sm text-muted">{t("chat.sending")}</span>
      </div>
    </div>
  );
}

function AgentAvatar() {
  return (
    <div className="mt-0.5 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-[10px] bg-pantera-900">
      <SparkIcon />
    </div>
  );
}

function SparkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#fbb03b"
      strokeWidth="2"
      strokeLinecap="round"
    >
      <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M5.6 18.4l2.8-2.8M15.6 8.4l2.8-2.8" />
    </svg>
  );
}

function AgentBubble({ agent, responseId }: { agent: AgentResponse; responseId: string }) {
  const { t } = useTranslation();

  // Top-supplier rankings, top-product rankings and the monthly price
  // trend are still gated on ?debug=1 — they add noise without changing
  // the headline answer. The per-supplier price distribution stays on
  // by default in version-B because it surfaces the concrete vendor
  // quotes that the prose summarises, which is what users on the b2v
  // pilot wanted to see at a glance.
  const showAdvanced = useMemo(isAdvancedViewOn, []);
  const showSuppliers = showAdvanced && agent.supplier_rankings.length > 0;
  const showProducts = showAdvanced && (agent.product_rankings?.length ?? 0) > 0;
  const showPriceDist = (agent.product_prices_by_supplier?.length ?? 0) > 0;
  const showPriceTrend = showAdvanced && (agent.product_price_trend?.length ?? 0) > 0;

  return (
    <div className="mt-2 flex gap-3">
      <AgentAvatar />
      <div className="min-w-0 flex-1">
        <div className="mb-2 flex items-center gap-2">
          <span className="text-[13px] font-bold text-pantera-900">{t("chat.agent")}</span>
          <span className="text-[11px] text-muted">
            · {t("chat.latency", { ms: agent.latency_ms })}
          </span>
        </div>

        {/* Main response card */}
        <div className="card-agent p-5">
          <div className="eyebrow mb-2">{t("chat.agent")}</div>

          {/* Formatted answer — markdown rendered with GFM (bold, lists,
              tables, line breaks). Replaces the previous plain <p> which
              showed raw `**` and `1.` literals. */}
          {agent.answer_markdown ? (
            <div className="prose-answer text-[15px] leading-relaxed text-pantera-900">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>{agent.answer_markdown}</ReactMarkdown>
            </div>
          ) : (
            <p className="text-[15px] text-muted">{t("chat.noAnswer")}</p>
          )}

          {/* Stats chips */}
          <Stats
            stats={agent.stats}
            cost={agent.total_cost_usd}
            tokens={agent.total_input_tokens + agent.total_output_tokens}
          />

          {/* Auxiliary tables — only render when the user's intent asks
              for that specific cut of the data. Each renders OPEN since
              the user explicitly requested it (no point hiding it
              behind a collapse). Everything else stays out of the way
              so the analyst sees prose + summary + matched UNSPSCs
              by default. */}
          {showSuppliers && (
            <CollapsibleSection
              title={t("chat.section.suppliers", "Top proveedores")}
              count={agent.supplier_rankings.length}
              defaultOpen={true}
              tone="purple"
            >
              <SupplierRankingTable rankings={agent.supplier_rankings} />
            </CollapsibleSection>
          )}

          {showProducts && (
            <CollapsibleSection
              title={t("chat.section.products", "Top productos")}
              count={agent.product_rankings.length}
              defaultOpen={true}
              tone="teal"
            >
              <ProductRankingTable rankings={agent.product_rankings} />
            </CollapsibleSection>
          )}

          {showPriceDist && (
            <CollapsibleSection
              title={t("chat.section.priceDistribution", "Precio por proveedor")}
              count={agent.product_prices_by_supplier.length}
              defaultOpen={true}
              tone="emerald"
            >
              <PriceDistributionTable rows={agent.product_prices_by_supplier} />
            </CollapsibleSection>
          )}

          {showPriceTrend && (
            <CollapsibleSection
              title={t("chat.section.priceTrend", "Tendencia mensual")}
              count={agent.product_price_trend.length}
              defaultOpen={true}
              tone="indigo"
            >
              <PriceTrendTable points={agent.product_price_trend} />
            </CollapsibleSection>
          )}

          {/* Verification */}
          {agent.verification.length > 0 && <VerificationPanel verification={agent.verification} />}

          {/* Audit feedback — one-click verdict the analyst can give to
              build a ground-truth dataset over time. Persists via the
              same `/admin/audit/responses/{id}/review` endpoint the full
              audit page uses. */}
          <AuditFeedbackBar responseId={responseId} />

          {/* Descriptions — now annotated with the UNSPSC that backs each
              text and a green "✓" / gray "✕" badge showing whether that
              UNSPSC survived the cascade SAT-name filter (KI-026). This
              lets the analyst see "yes, 'Complementarios' matched but
              its UNSPSC was Pesaje, so I dropped it from the price
              aggregate" without opening the debug panel. */}
          {agent.descriptions.length > 0 && (
            <details className="mt-4 text-xs text-muted">
              <summary className="cursor-pointer font-semibold text-pantera-700 hover:text-pantera-500">
                {t("chat.matchedDescriptions", { count: agent.descriptions.length })}
              </summary>
              {(() => {
                const appliedSet = new Set(agent.filters.clave_prod_serv ?? []);
                return (
                  <ul className="mt-2 space-y-1 pl-4">
                    {agent.descriptions.slice(0, 12).map((d) => {
                      const code = d.most_common_clave_prod_serv;
                      const applied = code !== null && code !== "01010101" && appliedSet.has(code);
                      const dropped = code !== null && code !== "01010101" && !applied;
                      return (
                        <li key={d.desc_hash_hex} className={dropped ? "opacity-60" : ""}>
                          <span className="font-mono text-[10px] text-muted/60">
                            {d.rrf_score.toFixed(4)}
                          </span>{" "}
                          {applied && (
                            <span
                              className="mr-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700"
                              title={t(
                                "chat.matchedDescriptions.appliedTooltip",
                                "Esta descripción contribuyó a las estadísticas de precios — su UNSPSC sobrevivió el filtro SAT",
                              )}
                            >
                              ✓ usada
                            </span>
                          )}
                          {dropped && (
                            <span
                              className="mr-1 rounded-full bg-rose-500/10 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700"
                              title={t(
                                "chat.matchedDescriptions.droppedTooltip",
                                "Esta descripción NO contribuyó a las estadísticas — su UNSPSC se descartó por irrelevancia con la consulta",
                              )}
                            >
                              ✕ descartada
                            </span>
                          )}
                          {d.text} —{" "}
                          <span className="text-muted">{d.occurrences.toLocaleString()}</span>
                          {code && code !== "01010101" && (
                            <span className="ml-2 font-mono text-[10px] text-pantera-700/70">
                              UNSPSC {code}
                            </span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                );
              })()}
            </details>
          )}

          {/* UNSPSC codes panel — replaces the legacy "Sample CFDI rows"
              table (which exposed raw RFC + precio + descripción tuples,
              violating RFP §6 "Solo se mostrará información procesada").
              Shows the analyst the SAT UNSPSCs that drove this retrieval
              (the canonical key the user kept asking us to make visible),
              with the official SAT name when available and a badge for
              any UNSPSC that was applied as cascade filter. */}
          <UnspscBreakdown
            descriptions={agent.descriptions}
            productRankings={agent.product_rankings}
            appliedFilter={agent.filters.clave_prod_serv}
          />

          {/* Debug panel — hidden by default to keep the analyst view simple
              (RFP §6 confidentiality + cliente 2026-05-18 "demasiada
              estadística"). Append ?debug=1 to the URL to surface it. */}
          {typeof window !== "undefined" &&
            new URLSearchParams(window.location.search).has("debug") && (
              <DebugPanel agent={agent} />
            )}
        </div>
      </div>
    </div>
  );
}

function Stats({
  stats,
  cost,
  tokens,
}: {
  stats: AgentResponse["stats"];
  cost: number;
  tokens: number;
}) {
  const { t } = useTranslation();
  // 2026-05-18 simplification: lead with proveedores (RFP Etapa 1), then
  // avg price. The matched_rows chip can intimidate users with
  // "1,000,000" for corpus-wide queries; show it as the last (smaller)
  // chip so the eye anchors on suppliers + price first. Cost/tokens
  // hidden by default — surface them on ?debug=1.
  const showDebugChips =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("debug");
  const chips = [
    {
      label: t("chat.stat.suppliers"),
      value: stats.unique_suppliers.toLocaleString(),
    },
    {
      label: t("chat.stat.avgPrice"),
      value: stats.avg_valor_unitario === null ? "—" : stats.avg_valor_unitario.toFixed(2),
    },
    {
      label: t("chat.stat.matched"),
      value: stats.matched_rows.toLocaleString(),
    },
    ...(showDebugChips
      ? [
          {
            label: t("chat.stat.cost"),
            value: `$${cost.toFixed(4)} (${tokens.toLocaleString()} tok)`,
          },
        ]
      : []),
  ];

  return (
    <div className="mt-5">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        Resumen
      </div>
      <dl
        className={
          chips.length > 3 ? "grid grid-cols-2 gap-2 md:grid-cols-4" : "grid grid-cols-3 gap-2"
        }
      >
        {chips.map((c) => (
          <div
            key={c.label}
            className="flex items-center gap-3 rounded-card border border-accent-400 bg-accent-400/15 p-3"
          >
            <div>
              <dd className="text-lg font-extrabold leading-none text-pantera-900">{c.value}</dd>
              <dt className="mt-1 text-[11px] text-muted">{c.label}</dt>
            </div>
          </div>
        ))}
      </dl>
    </div>
  );
}

function SupplierRankingTable({ rankings }: { rankings: SupplierRanking[] }) {
  const { t } = useTranslation();
  const maxSpend = Math.max(...rankings.map((r) => r.total_spend), 1);

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        <span>Proveedores relevantes</span>
        <span className="font-bold text-pantera-700">
          {t("chat.ranking.title", { count: rankings.length })}
        </span>
      </div>
      <div className="rounded-card border border-pantera-100 bg-pantera-50 py-1">
        {rankings.map((r, idx) => (
          <div
            key={r.rfc_emisor}
            className="grid items-center gap-3 px-4 py-2.5"
            style={{ gridTemplateColumns: "24px 1fr 1fr 60px" }}
          >
            <div className="text-[13px] font-bold text-pantera-700">
              {String(idx + 1).padStart(2, "0")}
            </div>
            <div>
              <div className="text-[13px] font-semibold text-pantera-900">{r.nombre_emisor}</div>
              <div className="font-mono text-[10px] text-muted">{r.rfc_emisor}</div>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-pantera-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(r.total_spend / maxSpend) * 100}%`,
                  background: "linear-gradient(90deg,#3b037d,#7c3aed)",
                }}
              />
            </div>
            <div className="text-right font-mono text-[12px] font-bold text-pantera-700">
              {r.transaction_count.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-1 grid grid-cols-4 gap-3 px-4 text-[10px] uppercase tracking-wide text-muted">
        <span>#</span>
        <span>{t("chat.ranking.col.supplier")}</span>
        <span>{t("chat.ranking.col.totalSpend")}</span>
        <span className="text-right">{t("chat.ranking.col.transactions")}</span>
      </div>
    </div>
  );
}

function ProductRankingTable({ rankings }: { rankings: ProductRanking[] }) {
  const { t } = useTranslation();
  const maxSpend = Math.max(...rankings.map((r) => r.total_spend), 1);

  // Each row is now a SAT UNSPSC code — the canonical product identifier.
  // The header shows: rank, UNSPSC code (monospaced/prominent), SAT
  // official name, supplier+description-variant counts, price range,
  // spend bar, and transaction count. The UNSPSC code is the visual
  // anchor of the row because it's how the analyst should reference
  // the product in follow-up queries.
  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        <span>{t("chat.productRanking.title", "Productos por código SAT (UNSPSC)")}</span>
        <span className="font-bold text-pantera-700">
          {t("chat.productRanking.count", {
            count: rankings.length,
            defaultValue: "{{count}} código(s)",
          })}
        </span>
      </div>
      <div className="rounded-card border border-pantera-100 bg-pantera-50 py-1">
        {rankings.slice(0, 30).map((p, idx) => (
          <div
            key={p.desc_hash_hex}
            className="grid items-center gap-3 px-4 py-2.5"
            style={{ gridTemplateColumns: "24px 92px 1fr 1fr 80px" }}
          >
            <div className="text-[13px] font-bold text-pantera-700">
              {String(idx + 1).padStart(2, "0")}
            </div>
            <div
              className="font-mono text-[12px] font-bold text-pantera-900"
              title={t(
                "chat.productRanking.unspscTooltip",
                "Código SAT UNSPSC — identificador oficial del producto",
              )}
            >
              {p.most_common_clave_prod_serv ?? "—"}
            </div>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-semibold text-pantera-900" title={p.text}>
                {p.text}
              </div>
              <div className="text-[10px] text-muted">
                {p.unique_suppliers}{" "}
                {t("chat.productRanking.uniqueSuppliers", {
                  count: p.unique_suppliers,
                  defaultValue: "proveedor(es)",
                })}
                {p.unique_descriptions > 1 && (
                  <>
                    {" · "}
                    <span
                      title={t(
                        "chat.productRanking.variantsTooltip",
                        "Cuántas descripciones distintas de CFDI se unificaron bajo este UNSPSC",
                      )}
                    >
                      {p.unique_descriptions}{" "}
                      {t("chat.productRanking.variants", {
                        defaultValue: "variante(s) de descripción",
                      })}
                    </span>
                  </>
                )}
                {" · "}${p.min_unit_price.toFixed(2)} → ${p.max_unit_price.toFixed(2)}
              </div>
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-pantera-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(p.total_spend / maxSpend) * 100}%`,
                  background: "linear-gradient(90deg,#0f766e,#22d3ee)",
                }}
              />
            </div>
            <div className="text-right font-mono text-[12px] font-bold text-pantera-700">
              {p.transaction_count.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      <div
        className="mt-1 grid gap-3 px-4 text-[10px] uppercase tracking-wide text-muted"
        style={{ gridTemplateColumns: "24px 92px 1fr 1fr 80px" }}
      >
        <span>#</span>
        <span>{t("chat.productRanking.col.unspsc", "UNSPSC")}</span>
        <span>
          {t(
            "chat.productRanking.col.product",
            "Nombre SAT · Proveedores / variantes / rango precio",
          )}
        </span>
        <span>{t("chat.productRanking.col.totalSpend", "Gasto total")}</span>
        <span className="text-right">{t("chat.productRanking.col.transactions", "Txs")}</span>
      </div>
    </div>
  );
}

function PriceDistributionTable({ rows }: { rows: ProductPriceBySupplier[] }) {
  const { t } = useTranslation();
  // The first row is the cheapest supplier — highlight it as the "answer".
  //
  // KI-023 (Phase-1 unit normalization): we surface a "Precio por
  // unidad base" column whenever any supplier has at least 50 % of
  // their rows carrying a normalizable ClaveUnidad. When no rows
  // qualify we skip the column entirely so the table stays narrow.
  const anyNormalizable = rows.some(
    (r) => r.base_unit !== null && (r.normalizable_pct ?? 0) >= 50 && r.avg_price_per_base !== null,
  );

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        <span>
          {t("chat.priceDistribution.title", "Precio por proveedor (producto identificado)")}
        </span>
        <span className="font-bold text-pantera-700">
          {t("chat.priceDistribution.count", {
            count: rows.length,
            defaultValue: "{{count}} proveedor(es)",
          })}
        </span>
      </div>
      <div className="overflow-x-auto rounded-card border border-pantera-100">
        <table className="w-full table-auto text-left text-[12px]">
          <thead className="bg-pantera-50 text-[10px] uppercase tracking-wide text-muted">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">{t("chat.priceDistribution.col.supplier", "Proveedor")}</th>
              <th className="px-3 py-2 text-right">{t("chat.priceDistribution.col.min", "Mín")}</th>
              <th className="px-3 py-2 text-right">
                {t("chat.priceDistribution.col.avg", "Promedio")}
              </th>
              <th className="px-3 py-2 text-right">{t("chat.priceDistribution.col.max", "Máx")}</th>
              {anyNormalizable && (
                <th className="px-3 py-2 text-right">
                  {t("chat.priceDistribution.col.perBase", "Precio por unidad base")}
                </th>
              )}
              <th className="px-3 py-2 text-right">
                {t("chat.priceDistribution.col.transactions", "Ventas")}
              </th>
              <th className="px-3 py-2 text-right">
                {t("chat.priceDistribution.col.totalSpend", "Gasto total")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const showPerBase =
                anyNormalizable &&
                r.base_unit !== null &&
                (r.normalizable_pct ?? 0) >= 50 &&
                r.avg_price_per_base !== null;
              return (
                <tr
                  key={r.rfc_emisor}
                  className={`border-t border-pantera-100 ${idx === 0 ? "bg-emerald-50/60" : ""}`}
                >
                  <td className="px-3 py-2 font-bold text-pantera-700">
                    {String(idx + 1).padStart(2, "0")}
                    {idx === 0 && (
                      <span className="ml-1 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                        {t("chat.priceDistribution.cheapestBadge", "Más barato")}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <div className="text-[12px] font-semibold text-pantera-900">
                      {r.nombre_emisor}
                    </div>
                    <div className="font-mono text-[10px] text-muted">{r.rfc_emisor}</div>
                  </td>
                  <td className="px-3 py-2 text-right font-mono font-bold text-emerald-700">
                    ${r.min_unit_price.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-pantera-900">
                    ${r.avg_unit_price.toFixed(2)}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-muted">
                    ${r.max_unit_price.toFixed(2)}
                  </td>
                  {anyNormalizable && (
                    <td className="px-3 py-2 text-right font-mono text-pantera-900">
                      {showPerBase ? (
                        <>
                          <span className="font-bold">
                            ${(r.avg_price_per_base ?? 0).toFixed(2)}
                          </span>
                          <span className="ml-1 text-[10px] text-muted">/{r.base_unit}</span>
                          <div className="text-[9px] text-muted/70">
                            ${(r.min_price_per_base ?? 0).toFixed(2)}-$
                            {(r.max_price_per_base ?? 0).toFixed(2)}
                          </div>
                        </>
                      ) : (
                        <span className="text-muted/50">—</span>
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2 text-right font-mono text-pantera-700">
                    {r.transaction_count.toLocaleString()}
                  </td>
                  <td className="px-3 py-2 text-right font-mono text-pantera-700">
                    ${r.total_spend.toFixed(2)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PriceTrendTable({ points }: { points: ProductPriceTrendPoint[] }) {
  const { t } = useTranslation();
  // Simple bar series approximating a price chart — each row shows the
  // monthly avg as a horizontal bar relative to the global max so the
  // analyst can spot trends without rendering a full charting library.
  const globalMax = Math.max(...points.map((p) => p.avg_unit_price), 1);

  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        <span>{t("chat.priceTrend.title", "Tendencia mensual de precios")}</span>
        <span className="font-bold text-pantera-700">
          {t("chat.priceTrend.count", { count: points.length, defaultValue: "{{count}} mes(es)" })}
        </span>
      </div>
      <div className="rounded-card border border-pantera-100 bg-pantera-50 py-1">
        {points.map((p) => (
          <div
            key={p.month}
            className="grid items-center gap-3 px-4 py-2"
            style={{ gridTemplateColumns: "90px 100px 1fr 80px 60px" }}
          >
            <div className="font-mono text-[11px] text-pantera-900">{p.month}</div>
            <div className="text-right font-mono text-[11px] font-bold text-pantera-700">
              ${p.avg_unit_price.toFixed(2)}
            </div>
            <div className="h-1.5 overflow-hidden rounded-full bg-pantera-100">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${(p.avg_unit_price / globalMax) * 100}%`,
                  background: "linear-gradient(90deg,#7c3aed,#22d3ee)",
                }}
              />
            </div>
            <div className="text-right font-mono text-[10px] text-muted">
              ${p.min_unit_price.toFixed(2)}-${p.max_unit_price.toFixed(2)}
            </div>
            <div className="text-right font-mono text-[10px] text-muted">
              {p.transaction_count.toLocaleString()}
            </div>
          </div>
        ))}
      </div>
      <div
        className="mt-1 grid gap-3 px-4 text-[10px] uppercase tracking-wide text-muted"
        style={{ gridTemplateColumns: "90px 100px 1fr 80px 60px" }}
      >
        <span>{t("chat.priceTrend.col.month", "Mes")}</span>
        <span className="text-right">{t("chat.priceTrend.col.avg", "Promedio")}</span>
        <span>{t("chat.priceTrend.col.bar", "Tendencia")}</span>
        <span className="text-right">{t("chat.priceTrend.col.range", "Rango")}</span>
        <span className="text-right">{t("chat.priceTrend.col.txs", "Txs")}</span>
      </div>
    </div>
  );
}

/**
 * UNSPSC breakdown panel. Replaces the legacy "Sample CFDI rows" table
 * that exposed raw RFC + price + free-text tuples (RFP §6 violation).
 *
 * Aggregates the SAT UNSPSC codes that surfaced from the description
 * matches — each row shows the code (the canonical product identifier
 * JP wanted prominent), the SAT official name when we can resolve it
 * via the product ranking, how many variants of free-text descriptions
 * collapsed under it, total CFDI occurrences, and a badge for codes
 * the cascade UNSPSC filter actually used (KI-016) so the analyst sees
 * exactly which classifications drove the answer.
 */
function UnspscBreakdown({
  descriptions,
  productRankings,
  appliedFilter,
}: {
  descriptions: DescriptionMatch[];
  productRankings: ProductRanking[];
  appliedFilter: string[] | null;
}) {
  const { t } = useTranslation();

  // SAT name lookup: the product ranking already carries the official
  // SAT label per UNSPSC (cfdi_producto_servicio.descripcion). Build a
  // map so we can show real names instead of just the 8-digit code.
  const satNameByCode = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of productRankings) {
      if (p.most_common_clave_prod_serv && p.text) {
        map.set(p.most_common_clave_prod_serv, p.text);
      }
    }
    return map;
  }, [productRankings]);

  type Row = {
    code: string;
    satName: string | null;
    variants: number;
    occurrences: number;
    applied: boolean;
  };

  const rows = useMemo<Row[]>(() => {
    const appliedSet = new Set(appliedFilter ?? []);
    const grouped = new Map<string, { variants: number; occurrences: number }>();
    for (const d of descriptions) {
      const code = d.most_common_clave_prod_serv;
      // Skip "01010101" (SAT placeholder "No existe en el catálogo")
      // and missing codes — those add noise, not signal.
      if (!code || code === "01010101") continue;
      const cur = grouped.get(code) ?? { variants: 0, occurrences: 0 };
      cur.variants += 1;
      cur.occurrences += d.occurrences;
      grouped.set(code, cur);
    }
    return Array.from(grouped.entries())
      .map(([code, agg]) => ({
        code,
        satName: satNameByCode.get(code) ?? null,
        variants: agg.variants,
        occurrences: agg.occurrences,
        applied: appliedSet.has(code),
      }))
      .sort((a, b) => {
        // Codes the cascade filter applied sort first; ties broken by
        // total occurrences. That way the analyst sees "what we searched"
        // before "what else matched".
        if (a.applied !== b.applied) return a.applied ? -1 : 1;
        return b.occurrences - a.occurrences;
      });
  }, [descriptions, satNameByCode, appliedFilter]);

  if (rows.length === 0) return null;

  return (
    <details className="mt-4 text-xs text-muted" open>
      <summary className="cursor-pointer font-semibold text-pantera-700 hover:text-pantera-500">
        {t("chat.unspscBreakdown.title", {
          count: rows.length,
          defaultValue: "Códigos UNSPSC encontrados ({{count}})",
        })}
      </summary>
      <p className="mt-1 text-[11px] text-muted/80">
        {t(
          "chat.unspscBreakdown.help",
          "El UNSPSC es el identificador SAT del producto. Los códigos marcados como aplicados son los que filtraron la búsqueda.",
        )}
      </p>
      <div className="mt-2 overflow-x-auto rounded-lg border border-pantera-100">
        <table className="w-full table-auto text-left">
          <thead className="bg-pantera-50 text-[10px] uppercase tracking-wide text-muted">
            <tr>
              <th className="px-3 pb-2 pt-2">
                {t("chat.unspscBreakdown.col.code", "Código UNSPSC")}
              </th>
              <th className="px-3 pb-2 pt-2">{t("chat.unspscBreakdown.col.name", "Nombre SAT")}</th>
              <th className="px-3 pb-2 pt-2 text-right">
                {t("chat.unspscBreakdown.col.variants", "Variantes")}
              </th>
              <th className="px-3 pb-2 pt-2 text-right">
                {t("chat.unspscBreakdown.col.occurrences", "Apariciones")}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((r) => (
              <tr
                key={r.code}
                className={`border-t border-pantera-100 ${r.applied ? "bg-emerald-50/60" : ""}`}
              >
                <td className="px-3 py-2 font-mono text-[12px] font-bold text-pantera-900">
                  {r.code}
                  {r.applied && (
                    <span className="ml-2 rounded-full bg-emerald-500/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-emerald-700">
                      {t("chat.unspscBreakdown.appliedBadge", "Aplicado")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-pantera-900">
                  {r.satName ?? (
                    <span className="text-muted/70">
                      {t("chat.unspscBreakdown.noName", "(sin nombre SAT)")}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono text-muted">{r.variants}</td>
                <td className="px-3 py-2 text-right font-mono text-pantera-700">
                  {r.occurrences.toLocaleString()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  );
}

/**
 * Collapsible wrapper for the auxiliary data tables (supplier ranking,
 * product ranking, price distribution, price trend). Header shows the
 * row count so the analyst sees what's hidden without opening. The tone
 * tints the left border so each section is visually distinct.
 */
type CollapsibleTone = "purple" | "teal" | "emerald" | "indigo";

const TONE_BORDER: Record<CollapsibleTone, string> = {
  purple: "border-l-pantera-500",
  teal: "border-l-teal-500",
  emerald: "border-l-emerald-500",
  indigo: "border-l-indigo-500",
};

function CollapsibleSection({
  title,
  count,
  defaultOpen,
  tone,
  children,
}: {
  title: string;
  count: number;
  defaultOpen: boolean;
  tone: CollapsibleTone;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <details
      className={`mt-4 rounded-card border border-pantera-100 border-l-4 ${TONE_BORDER[tone]} bg-white/60`}
      open={defaultOpen}
    >
      <summary className="cursor-pointer select-none px-3 py-2 text-[12px] font-semibold text-pantera-900 hover:bg-pantera-50">
        <span>{title}</span>
        <span className="ml-2 rounded-full bg-pantera-100 px-2 py-0.5 text-[10px] font-bold text-pantera-700">
          {count}
        </span>
        <span className="ml-2 text-[10px] font-normal text-muted">
          {defaultOpen
            ? t("chat.section.clickToCollapse", "click para ocultar")
            : t("chat.section.clickToExpand", "click para abrir")}
        </span>
      </summary>
      <div className="px-3 pb-3">{children}</div>
    </details>
  );
}

/**
 * One-click verdict capture under each agent answer. Persists via the
 * existing audit endpoint so a single feedback signal feeds the audit
 * dashboard AND seeds the long-term ground-truth dataset.
 *
 * Severity defaults to LOW since this is the lightweight inline path;
 * the full audit page is still available for nuanced reviews
 * (categories, comments, corrected response). Once a verdict is sent,
 * the bar shows the chosen state and offers a "change" affordance.
 */
function AuditFeedbackBar({ responseId }: { responseId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [submitted, setSubmitted] = useState<"correct" | "partial" | "incorrect" | null>(null);

  const mutation = useMutation({
    mutationFn: (verdict: "correct" | "partial" | "incorrect") =>
      submitAuditReview(responseId, {
        verdict,
        // Inline path is intentionally low-friction. The full audit page
        // (Auditoría tab) is where severity / categories / comments live.
        severity: verdict === "incorrect" ? "medium" : verdict === "partial" ? "low" : "low",
        categories: [],
      }),
    onSuccess: (_, verdict) => {
      setSubmitted(verdict);
      // Refresh the audit queue cache so the response shows up in the
      // admin dashboard with the new verdict, no manual reload needed.
      void queryClient.invalidateQueries({ queryKey: ["admin", "audit", "queue"] });
    },
  });

  const buttons: Array<{
    verdict: "correct" | "partial" | "incorrect";
    label: string;
    icon: string;
    activeClass: string;
    idleClass: string;
  }> = [
    {
      verdict: "correct",
      label: t("chat.feedback.correct", "Correcto"),
      icon: "✓",
      activeClass: "bg-emerald-500 text-white",
      idleClass: "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
    },
    {
      verdict: "partial",
      label: t("chat.feedback.partial", "Parcial"),
      icon: "◐",
      activeClass: "bg-amber-500 text-white",
      idleClass: "border-amber-300 text-amber-700 hover:bg-amber-50",
    },
    {
      verdict: "incorrect",
      label: t("chat.feedback.incorrect", "Incorrecto"),
      icon: "✗",
      activeClass: "bg-rose-500 text-white",
      idleClass: "border-rose-300 text-rose-700 hover:bg-rose-50",
    },
  ];

  return (
    <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-pantera-100 pt-3">
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted">
        {submitted
          ? t("chat.feedback.submitted", "Tu evaluación")
          : t("chat.feedback.ask", "¿Te sirvió esta respuesta?")}
      </span>
      {buttons.map((b) => {
        const isActive = submitted === b.verdict;
        return (
          <button
            key={b.verdict}
            type="button"
            onClick={() => !mutation.isPending && mutation.mutate(b.verdict)}
            disabled={mutation.isPending}
            aria-pressed={isActive}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[12px] font-semibold transition ${
              isActive ? b.activeClass : `bg-white ${b.idleClass}`
            } ${mutation.isPending ? "cursor-wait opacity-60" : ""}`}
          >
            <span aria-hidden>{b.icon}</span>
            <span>{b.label}</span>
          </button>
        );
      })}
      {mutation.isError && (
        <span className="text-[11px] text-rose-600">
          {t("chat.feedback.error", "No se pudo guardar; intenta de nuevo.")}
        </span>
      )}
      {submitted && !mutation.isPending && (
        <span className="text-[11px] text-muted">
          {t("chat.feedback.thanks", "Guardado en la cola de auditoría.")}
        </span>
      )}
    </div>
  );
}

function VerificationPanel({ verification }: { verification: VerificationClaim[] }) {
  const { t } = useTranslation();
  const counts = verification.reduce(
    (acc, c) => {
      acc[c.verdict] = (acc[c.verdict] ?? 0) + 1;
      return acc;
    },
    { verified: 0, unsupported: 0, out_of_scope: 0 } as Record<
      VerificationClaim["verdict"],
      number
    >,
  );
  const hasUnsupported = counts.unsupported > 0;

  return (
    <div className="mt-5">
      <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-muted">
        Verificación
      </div>
      {hasUnsupported && (
        <p className="mb-2 rounded-card border border-error/20 bg-error/5 px-3 py-2 text-xs text-error">
          {t("chat.verification.warning")}
        </p>
      )}
      <ul className="flex flex-col gap-1.5">
        {verification.map((c, idx) => {
          const color =
            c.verdict === "verified"
              ? "#10b981"
              : c.verdict === "unsupported"
                ? "#ef4444"
                : "#fbb03b";
          return (
            <li
              key={`${c.kind}-${c.value}-${idx}`}
              className="flex items-start gap-2.5 text-[13px] text-pantera-900"
              title={`${t(`chat.verification.kind.${c.kind}`)} · ${c.reason}`}
            >
              <span
                className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full"
                style={{ background: color }}
              >
                <svg
                  width="8"
                  height="8"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="3.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m4 12 5 5 11-11" />
                </svg>
              </span>
              <span>
                <span className="mr-1 text-[10px] font-bold uppercase tracking-wide opacity-60">
                  {t(`chat.verification.kind.${c.kind}`)}
                </span>
                {c.text}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function DebugPanel({ agent }: { agent: AgentResponse }) {
  const { t } = useTranslation();
  const traces = agent.trace ?? [];
  const queryConceptsTrace = traces.find((t) => t.step === "query_concepts");
  const rankingTrace = traces.find((t) => t.step === "top_suppliers");

  const selectSql = (queryConceptsTrace?.detail?.select_sql as string | undefined) ?? null;
  const aggregateSql = (queryConceptsTrace?.detail?.aggregate_sql as string | undefined) ?? null;
  const params =
    (queryConceptsTrace?.detail?.params as Record<string, unknown> | undefined) ?? null;
  const rankingSql = (rankingTrace?.detail?.ranking_sql as string | undefined) ?? null;
  const rankingParams =
    (rankingTrace?.detail?.ranking_params as Record<string, unknown> | undefined) ?? null;

  return (
    <details className="mt-4 text-xs text-muted">
      <summary className="cursor-pointer font-semibold text-pantera-700 hover:text-pantera-500">
        {t("chat.debug.title")}
      </summary>
      <div className="mt-2 space-y-2 rounded-card border border-pantera-100 bg-pantera-50 p-3">
        <DebugSection title={t("chat.debug.filters")}>
          <pre className="overflow-x-auto rounded-lg bg-white p-2 text-[10px] text-pantera-900">
            {JSON.stringify(agent.filters, null, 2)}
          </pre>
        </DebugSection>

        {selectSql && (
          <DebugSection title={t("chat.debug.selectSql")}>
            <SqlBlock sql={selectSql} params={params} />
          </DebugSection>
        )}
        {aggregateSql && (
          <DebugSection title={t("chat.debug.aggregateSql")}>
            <SqlBlock sql={aggregateSql} params={params} />
          </DebugSection>
        )}
        {rankingSql && (
          <DebugSection title={t("chat.debug.rankingSql")}>
            <SqlBlock sql={rankingSql} params={rankingParams} />
          </DebugSection>
        )}

        <DebugSection title={t("chat.debug.trace")}>
          <table className="w-full table-auto text-left text-[11px]">
            <thead className="text-[10px] uppercase tracking-wide text-muted">
              <tr>
                <th className="pb-1 pr-2">{t("chat.debug.col.step")}</th>
                <th className="pb-1 pr-2 text-right">{t("chat.debug.col.startMs")}</th>
                <th className="pb-1 pr-2 text-right">{t("chat.debug.col.durationMs")}</th>
              </tr>
            </thead>
            <tbody>
              {traces.map((tr, idx) => (
                <tr key={`${tr.step}-${idx}`} className="border-t border-pantera-100">
                  <td className="py-1 pr-2 font-mono">{tr.step}</td>
                  <td className="py-1 pr-2 text-right font-mono">
                    {tr.started_at_ms.toLocaleString()}
                  </td>
                  <td className="py-1 pr-2 text-right font-mono">
                    {tr.duration_ms.toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </DebugSection>

        {agent.cited_concepto_ids.length > 0 && (
          <DebugSection title={t("chat.debug.citations")}>
            <p className="text-[10px] text-muted">
              <strong>concepto_id:</strong> {agent.cited_concepto_ids.slice(0, 20).join(", ")}
              {agent.cited_concepto_ids.length > 20 && "…"}
            </p>
            {agent.cited_uuids.length > 0 && (
              <p className="mt-1 text-[10px] text-muted">
                <strong>uuid_cfdi:</strong> {agent.cited_uuids.slice(0, 5).join(", ")}
                {agent.cited_uuids.length > 5 && "…"}
              </p>
            )}
          </DebugSection>
        )}
      </div>
    </details>
  );
}

function DebugSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h5 className="text-[10px] font-semibold uppercase tracking-wide text-pantera-700">
        {title}
      </h5>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function SqlBlock({ sql, params }: { sql: string; params: Record<string, unknown> | null }) {
  return (
    <div className="space-y-1">
      <pre className="max-h-40 overflow-auto rounded-lg bg-pantera-900 p-2 font-mono text-[10px] leading-relaxed text-pantera-100">
        {sql}
      </pre>
      {params && Object.keys(params).length > 0 && (
        <pre className="overflow-x-auto rounded-lg bg-white p-2 text-[10px] text-pantera-900">
          {JSON.stringify(params, null, 2)}
        </pre>
      )}
    </div>
  );
}

// Re-export type alias for the unused-import linter.
export type { AgentTrace };
